/**
 * The user directory's wire layer — the type-ahead behind EVERY "who?" question.
 *
 * ===========================================================================
 * WHY THIS IS ITS OWN MODULE AND NOT A SECOND COPY (ESC-DIRECTORY)
 * ===========================================================================
 * This code was written inside `features/inquiries/` because the bulk Assign menu
 * was the first surface that needed it. It is now also the work-order assign
 * popover's picker, and it will be Tally Sales' too. The choice at that point is
 * one module or two copies, and two copies of a SEARCH CAP is precisely how the
 * "showing N of M" honesty rots: one surface gets a fix, the other keeps lying,
 * and nothing fails to tell anybody.
 *
 * So the CONTRACT lives here once — the cap, the minimum term, the query key, and
 * the shape the server actually sends. The COPY stays with each surface, because a
 * dropdown section and a record-page popover word the same fact differently and
 * neither should inherit the other's sentence.
 *
 * Nothing here is inquiry-shaped, which is the test of whether the move was right:
 * `GET /users` knew nothing about inquiries before this file existed.
 */

import { apiRequest } from '../../lib/api';
import type { Paged } from '../../lib/inquiries-api';
import type { CountryCode } from '../../domain/countries';

/**
 * `GET /users` as the colleague picker needs it — transcribed from `UserView`
 * (`apps/api/src/modules/users/dto/users.dto.ts:143`).
 *
 * `phone`, `last_login_at` and `roles[].role` are in the API shape and are deliberately
 * NOT transcribed, for the same reason `PartyDto` drops its §9.2 discriminators: no
 * picker renders them, and a field this module claims to know but never reads is a field
 * that will be wrong the first time somebody trusts it.
 *
 * `is_group_admin` and `roles[].country` are not decoration. They are the ONLY two facts
 * the out-of-scope prediction is made from (owner ruling 3 / C6), and the API is the only
 * place either of them exists — the access token carries the caller's scope, never anybody
 * else's.
 */
export interface DirectoryUserDto {
  id: number;
  name: string;
  email: string;
  status: 'active' | 'disabled';
  is_group_admin: boolean;
  roles: Array<{ country: CountryCode }>;
}

/**
 * How many MATCHES one search shows. This is a type-ahead, not the admin list.
 *
 * ===========================================================================
 * WHY THIS IS A SEARCH CAP AND NOT A PAGE SIZE (ESC-DIRECTORY, owner-ruled)
 * ===========================================================================
 * This constant used to mean "page 1 of every active account", and that was a
 * permanent invisibility bug: past 50 active users the picker simply could not
 * offer somebody, and flipping the server's `ORDER BY` direction only chose
 * whether the newest or the longest-serving vanished. Both halves were caught
 * by ratified smokes that could not both pass — measured at 57 active users,
 * with the pilot cast at ranks 53-57.
 *
 * A cap on SEARCH RESULTS does not have that failure: any account is reachable
 * by typing its name, at any directory size, whatever the server's ordering.
 * The cap only bounds how many matches one query returns, and when it bites the
 * UI SAYS SO ("Showing the first N of M matches") — a truncated list that does
 * not admit it is a silent failure (rule 11).
 *
 * Twenty, not fifty: a menu the operator scrolls past twenty rows of is one they
 * should have typed another letter into, and every rendered row costs an avatar
 * request. The server permits up to 100 (`MAX_PER_PAGE`), so this is our budget,
 * not the server's limit.
 */
export const DIRECTORY_PAGE = 20;

/**
 * Below this many characters the picker does not search at all.
 *
 * Same threshold, same reason as the party picker (`usePartySearch`): a
 * one-character `LIKE '%a%'` is a table scan that returns most of the directory
 * — which is precisely the useless everybody-list this redesign exists to end.
 */
export const MIN_COLLEAGUE_SEARCH = 2;

/**
 * The colleague list behind every assign surface.
 *
 * ===========================================================================
 * NOT NARROWED BY `?country=`, ON PURPOSE
 * ===========================================================================
 * `ListUsersQueryDto` accepts a `country`, and passing the current one would hide exactly
 * the people this feature exists for: the colleague who is NOT scoped here. The out-of-scope
 * marker and its confirm would then be unreachable by construction, and the first time
 * anybody saw the server's `warnings[]` would be in production.
 *
 * The caller is not therefore seeing more than they may. `UsersService.visibleUserIds()`
 * already narrows the list server-side — a `grp:true` caller sees everybody, a
 * country-scoped `users.manage` holder sees only users with a role in a country they
 * manage. Which means, and this is worth stating rather than discovering: for a
 * single-country Country Manager the list can only contain in-scope people, so the marker is
 * genuinely exercised by group admins and by admins who manage two countries. That is why
 * the response's `warnings[]` stays authoritative (C6) instead of the client's prediction.
 *
 * `status: 'active'` IS applied. A disabled account cannot sign in at all, so assigning work
 * to one parks it where nobody can pick it up — the same failure as the out-of-scope case
 * and a worse one, and the API raises no advisory for it.
 */
export async function fetchColleagues(
  term: string,
  signal?: AbortSignal,
): Promise<Paged<DirectoryUserDto>> {
  return apiRequest<Paged<DirectoryUserDto>>('/users', {
    query: { q: term, status: 'active', page: 1, perPage: DIRECTORY_PAGE },
    signal,
  });
}

/**
 * One colleague, by id — name and avatar flag, nothing else.
 *
 * `GET /users/:id/card` is `crf.view` (OQ-WO-ASSIGNEE-NAME, owner-ruled), which is
 * what lets a work board say who is on a card. It is NOT the directory search: a
 * caller with `crf.view` and no `users.manage` may resolve an id they already
 * hold and may not enumerate anybody.
 */
export interface UserCardDto {
  id: string;
  name: string;
  has_avatar: boolean;
}

export async function fetchUserCard(userId: string, signal?: AbortSignal): Promise<UserCardDto> {
  return apiRequest<UserCardDto>(`/users/${userId}/card`, { signal });
}

export const userKeys = {
  /**
   * No country in the key. The request carries none (see above) and the out-of-scope
   * marking is derived client-side, so one cached result serves every country the
   * caller can switch to — switching country must not refetch a list that cannot differ.
   *
   * The TERM is in the key, so each distinct search is its own cache entry and
   * retyping a term the operator already tried answers instantly.
   *
   * No SURFACE in the key either: the bulk menu and the work-order popover asking
   * the same question get the same cached answer, which is the point of one module.
   */
  search: (term: string) => ['users', 'directory', term] as const,
  /**
   * A single card. Separate from `search` so a resolved assignee is not evicted
   * every time somebody types in a picker.
   */
  card: (userId: string) => ['users', 'card', userId] as const,
};
